import axios from 'axios';
import { runSegmentGeocode } from '@actions/data-detail-actions';
import { BASE_API_URL, getDetailAPIRequestUrl } from '@constants/endpoints';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { decodeDetails, convertCategoriesToFields } from '@utils/data-detail-utils';

const wait = delay => new Promise(resolve => setTimeout(resolve, delay));

// Run processItem (which returns a Promise) for the items in batches, waiting for each
// batch to complete and a delay before starting the next one, to rate limit the requests.
const processInBatches = async (items, processItem, batchSize, delay) => {
  for (let startIndex = 0; startIndex < items.length; startIndex += batchSize) {
    if (startIndex > 0) {
      await wait(delay);
    }
    await Promise.all(items.slice(startIndex, startIndex + batchSize).map(processItem));
  }
};

// Create a promise for each row (by row id), and the functions to resolve them.
const createRowPromises = rows => {
  const promises = {};
  const resolvers = {};
  rows.forEach(row => {
    promises[row.id] = new Promise(resolve => {
      resolvers[row.id] = resolve;
    });
  });
  return { promises, resolvers };
};

// Segment error when a location with address or geocoding data can't be geocoded.
const LOCATION_NOT_GEOCODED_ERROR = 'The location could not be geocoded from the addresses provided.';

// Standard segment address keys sent to the street_segment API.
const ADDRESS_GEOCODING_KEYS = [
  'on_street',
  'from_address',
  'from_street',
  'to_address',
  'to_street'
];

// Segment keys sent for layer geocoding (special case for Soundtransit).
const LAYER_GEOCODING_KEYS = [
  ...ADDRESS_GEOCODING_KEYS,
  'from_station',
  'to_station',
  'line_name'
];

const hasValue = value => value !== null && typeof value !== 'undefined' && value !== '';

// Return the segment values for the specified keys, skipping the empty ones.
const getSegmentParams = (segment, keys) => keys.reduce((params, key) => {
  if (hasValue(segment[key])) {
    params[key] = segment[key];
  }
  return params;
}, {});

// Geocoding result for a segment which could not be geocoded.
const segmentNotGeocoded = (segment, message) => ({
  segment,
  error: {
    shape: message || LOCATION_NOT_GEOCODED_ERROR
  }
});

// Geocode a segment with the street_segment API, using the specified params.
// Resolves with the geocoded segment, or with an error if it returns no location.
const requestStreetSegment = async (segment, params) => {
  try {
    const { data: results } = await axios.get(`${BASE_API_URL}/street_segment/`, { params });
    if (Array.isArray(results) && results.length > 0 && results[0].feature?.geometry) {
      const {feature, ...rest} = results[0];
      return {
        segment: {
          ...segment,
          ...rest,
          shape: feature.geometry
        },
        error: null
      };
    }
    return segmentNotGeocoded(segment);
  } catch (error) {
    return segmentNotGeocoded(segment, error?.response?.data?.error);
  }
};

// Geocode a segment with the street_segment API, sending its address and layer
// geocoding (i.e. station) values listed in geocodingFields, the same location data
// used to geocode on the final batch upload.
// Resolves with the segment (geocoded if it succeeded, i.e. it has a shape) and the
// error, if any. If the segment has no address or geocoding data, no request is made
// and it resolves with the unchanged segment and no error.
const geocodeSegmentWithStreetSegment = async (segment, geocodingFields) => {
  const params = getSegmentParams(segment, geocodingFields);
  if (Object.keys(params).length === 0) {
    return { segment, error: null };
  }
  return requestStreetSegment(segment, params);
};

// Geocode a segment with layer geocoding (special case for Soundtransit), sending its
// address and station values to the street_segment API.
// Resolves with the segment (geocoded if it succeeded) and the error, if any. If the
// segment has no address or station data, no request is made and it resolves with
// the unchanged segment and no error.
const geocodeSegmentWithLayer = async segment => {
  const params = getSegmentParams(segment, LAYER_GEOCODING_KEYS);
  if (Object.keys(params).length === 0) {
    return { segment, error: null };
  }
  return requestStreetSegment(segment, params);
};

// Geocode an address with Google, resolving with its coordinates, or null if it's
// empty or not found.
const geocodeAddress = async address => {
  if (!address) {
    return null;
  }
  try {
    const results = await geocodeByAddress(address);
    return await getLatLng(results[0]);
  } catch {
    return null;
  }
};

// Geocode a segment by geocoding its display addresses with Google, and computing the
// segment between those points (see runSegmentGeocode).
// Resolves with the segment (geocoded if it succeeded) and the error, if any. If the
// segment has no display addresses, no request is made and it resolves with the
// unchanged segment and no error.
const geocodeSegmentWithGoogle = async segment => {
  const { display_from, display_to } = segment;
  if (!hasValue(display_from) && !hasValue(display_to)) {
    return { segment, error: null };
  }

  const [start, end] = await Promise.all([geocodeAddress(display_from), geocodeAddress(display_to)]);
  // At least a start point is needed:
  if (!start) {
    return segmentNotGeocoded(segment);
  }
  try {
    const segmentWithShape = await runSegmentGeocode(start, end);
    return {
      segment: { ...segment, ...segmentWithShape },
      error: null
    };
  } catch {
    return segmentNotGeocoded(segment);
  }
};

// Geocode the segments of a row, resolving with the row update (the geocoded segments
// and their errors, if any).
const geocodeRow = async (row, geocodeSegment) => {
  const updatedSegments = await Promise.all(row.data.segments.map(segment => {
    if (segment.shape) {
      return { segment, error: null };
    }
    return geocodeSegment(segment);
  }));

  const segmentsObj = updatedSegments.map(item => {
    const updatedSegment = item.segment;

    // If we specified from_station or to_station for geocoding,
    // but got back only coordinates without address info,
    // set the title to the station name
    if ((updatedSegment.from_station || updatedSegment.to_station) &&
        (!updatedSegment.on_street && !updatedSegment.from_address &&
         !updatedSegment.from_street && !updatedSegment.to_address &&
         !updatedSegment.to_street)) {
      const stationName = updatedSegment.from_station || updatedSegment.to_station;
      return {
        ...updatedSegment,
        title: stationName
      };
    }

    return updatedSegment;
  });
  const errors = updatedSegments.map(item => item.error);
  const hasActualErrors = errors.some(error => error !== null);

  return {
    rowId: row.id,
    // Only update segments, since row.data might have changed due to processing.
    updates: { segments: segmentsObj },
    // eslint-disable-next-line no-undefined
    segmentErrors: hasActualErrors ? errors : undefined
  };
};

// We needed a way to wait for window.google.map.xxxx data to appear,
// since sometimes this code might run before the Google code is
// available on the javascript window.xxxx namespace.
const waitForGoogleMaps = async () => {
  while (!window.mapSetup) {
    await wait(100);  // Check every 100ms
  }
};

// Run geocoding for address data in the rows uploaded via batch upload.
//
// Returns an object with a Promise for each row that needs geocoding (by row id), which
// resolves with the row update ({ rowId, updates, segmentErrors }) when it's geocoded.
//
// geocodingFields is only set for v2 batch uploads (the location keys to send to
// the street_segment API, see geocodeSegmentWithStreetSegment).
// useLayerGeocoding is set if layer geocoding is enabled globally and for the entity type.
//
// Note: the v1 geocoding results are unreliable predictions of the final upload. v1
// uploads send only the address columns (not the shapes), and the backend geocodes
// them again with its own rules, so a location geocoded here (i.e. the straight line
// fallback of runSegmentGeocode, which has no address data) can still fail on the
// final step. The street_segment API also picks its lookup differently from the final
// step's normalizer geocoding (BaseRowDataNormalizer.geocode), i.e. for mixed cross
// street/address endpoints, both from_street and from_address set, from_on/to_on,
// annual permit alleys/coordinates, or global layer geocoding with the entity type
// flag off, so its result can differ too. Switching the entity type to a v2 batch
// configuration (which uploads the geocoded shapes) is the preferred fix.
export const geocodeBatchRows = (rows, geocodingFields = null, useLayerGeocoding = false) => {
  // Geocode a single segment:
  // - v2 batch uploads use the street_segment API with the segment's address data
  //   (which includes the layer geocoding columns). If it fails, layer geocoding
  //   entity types keep the result (and its error), others fall back to Google.
  // - v1 batch uploads use layer geocoding for layer geocoding entity types (with no
  //   fallback), or else the street_segment API with the standard address fields,
  //   falling back to Google if it fails.
  const geocodeSegment = async segment => {
    if (geocodingFields) {
      const result = await geocodeSegmentWithStreetSegment(segment, geocodingFields);
      if (result.segment.shape || useLayerGeocoding) {
        return result;
      }
      return geocodeSegmentWithGoogle(segment);
    }
    if (useLayerGeocoding) {
      return geocodeSegmentWithLayer(segment);
    }
    // Previously google geocode failures would be silent and send unchanged CSV address data to final (legacy) step.
    // The updated code enforces geocoding success, so we need to use geocodeSegmentWithStreetSegment to predict if
    // the final step will succeed and only show an error if neither geocoding method works.
    const result = await geocodeSegmentWithStreetSegment(segment, ADDRESS_GEOCODING_KEYS);
    if (result.segment.shape) {
      return result;
    }
    return geocodeSegmentWithGoogle(segment);
  };

  const rowsToGeocode = rows.filter(row => row.data.segments?.some(segment => !segment.shape));
  const { promises, resolvers } = createRowPromises(rowsToGeocode);

  // Launch the geocoding of rows in batches, with a delay (our backend can handle it
  // but I added this since I don't know if Google might rate limit us, if we
  // call geocodeByAddress() too often).
  const runGeocoding = async () => {
    await waitForGoogleMaps();
    await processInBatches(rowsToGeocode, async row => {
      resolvers[row.id](await geocodeRow(row, geocodeSegment));
    }, 10, 1000);
  };
  runGeocoding();

  return promises;
};

const ENTITY_FETCH_ERROR = 'The existing entity data could not be loaded for this row, ' +
  'uploading it could clear existing values. Skip this row, or upload the file again.';

// Fields that should not be overwritten with empty strings from CSV
// (these are identifiers that should come from backend if CSV doesn't have them)
const protectedFields = ['id'];

// Backend-only/read-only fields that should NOT be included in CSV output.
// These are generated by the API and cannot be set by the user.
const excludedBackendFields = [
  'created', 'modified', 'active', 'created_by', 'modified_by',
  'overlaps',
  'agency_type', 'agency_name', 'agency_type_name',
  'color',
  'conflict_status',
  'type_name', 'type_display_name',
  'owner_list', 'owner_dict',
  'undefined_streets', 'polygons',
  'datetime_parts',
  'group_ids',
  'checksum',
  'category_dict'  // This gets converted to |categoryName format, so exclude the original
];

// Merge backend data with CSV data
const mergeEntityData = (row, backendData, metadata, entityCategoryTypes) => {
  // The CSV missing fields use the existing entity data as fetched: all its fields are
  // kept, with the categories converted to the |categoryName format.
  const decodedBackendData = { ...backendData };
  Object.assign(
    decodedBackendData,
    convertCategoriesToFields(entityCategoryTypes, backendData.category_dict, true)
  );
  // Remove category_dict since we've converted it to |categoryName format
  delete decodedBackendData.category_dict;

  // Decode the fetched values using metadata (handles dates, datetimes, etc.), the same
  // processing done on the normal entity edit page. Only the filled values are decoded,
  // since decoding the empty ones would set the form defaults (and the metadata only
  // fields), which only apply to new entities.
  if (metadata) {
    const decoded = decodeDetails(backendData, metadata, entityCategoryTypes);
    Object.keys(backendData).forEach(key => {
      const value = backendData[key];
      if (key in decoded && value !== null && value !== void 0) { // eslint-disable-line no-void
        decodedBackendData[key] = decoded[key];
      }
    });
  }

  // Filter out backend-only fields that shouldn't be in CSV
  const filteredBackendData = {};
  Object.keys(decodedBackendData).forEach(key => {
    if (!excludedBackendFields.includes(key)) {
      filteredBackendData[key] = decodedBackendData[key];
    }
  });

  // Start with filtered backend data as base
  const mergedData = { ...filteredBackendData };

  // Overwrite with CSV data (CSV data has priority)
  Object.keys(row.data).forEach(key => {
    const csvValue = row.data[key];

    // Skip protected fields if CSV value is empty/falsy
    // (we want to keep the backend's ID, not overwrite with empty string)
    if (protectedFields.includes(key)) {
      if (csvValue !== null && csvValue !== void 0 && csvValue !== '') { // eslint-disable-line no-void
        mergedData[key] = csvValue;
      }
      return;
    }

    // For category fields (starting with |), only overwrite if CSV has a value
    if (key.startsWith('|')) {
      if (csvValue !== null && csvValue !== void 0) { // eslint-disable-line no-void
        mergedData[key] = csvValue;
      }
      return;
    }

    // For other fields, use CSV value if it's not null/undefined
    if (csvValue !== null && csvValue !== void 0) { // eslint-disable-line no-void
      mergedData[key] = csvValue;
    }
  });

  // Special handling for segments: CSV has address data, backend has shapes
  if (row.data.segments && row.data.segments.length > 0) {
    // Use segments from CSV but merge with backend segment data for shapes
    const csvSegments = row.data.segments;
    const backendSegments = backendData.segments || [];

    mergedData.segments = csvSegments.map((csvSeg, idx) => {
      // If there's a corresponding backend segment, merge shape if CSV doesn't have one
      if (idx < backendSegments.length && !csvSeg.shape) {
        return {
          ...csvSeg,
          // Keep backend shape if CSV doesn't have one (geocoding will override if needed)
          shape: backendSegments[idx].shape
        };
      }
      return csvSeg;
    });
  } else if (backendData.segments) {
    // If CSV has no segments at all, use backend segments
    mergedData.segments = backendData.segments;
  }

  return mergedData;
};

// Fetch entity by ID
const fetchById = async (entityType, entityId) => {
  const url = getDetailAPIRequestUrl(entityType, entityId);
  const response = await axios.get(url);
  return response.data;
};

// Fetch entity by external_id, returning null if there's no entity with that external_id.
const fetchByExternalId = async (entityType, externalId) => {
  // Use the attrs filter to search by external_id
  const url = `${BASE_API_URL}/map/?type=${entityType}&attrs=external_id|${encodeURIComponent(externalId)}`;
  const response = await axios.get(url);
  const results = response.data.results || response.data;
  if (Array.isArray(results) && results.length > 0) {
    // Return the first matching entity
    return results[0];
  }
  return null;
};

// Fetch the existing entity for a row (by ID first, then by external_id), resolving
// with the row update ({ rowId, updates, errors }), or null if there's nothing to update.
const fetchEntityRow = async (row, entityType, metadata, entityCategoryTypes) => {
  const entityId = row.data.id;
  const externalId = row.data.external_id;
  try {
    const backendData = entityId ?
      await fetchById(entityType, entityId) :
      await fetchByExternalId(entityType, externalId);
    // If entity not found, continue with CSV data only
    if (!backendData) {
      return null;
    }
    return {
      rowId: row.id,
      // Update the row with merged data
      updates: mergeEntityData(row, backendData, metadata, entityCategoryTypes)
    };
  } catch (error) {
    // If entity not found (404), continue with CSV data only
    if (error.response?.status === 404) {
      return null;
    }
    // eslint-disable-next-line no-console
    console.error(`Error fetching entity (id: ${entityId}, external_id: ${externalId}):`, error);
    // The row only holds the CSV data, uploading it could clear the existing
    // entity values (see convertBatchToFile), so flag it as an error that
    // blocks the upload until the row is skipped. This update is applied before
    // the geocoding one (see runBatchDataLoading), so the row errors are the loaded ones.
    const currentErrors = row.errors || {};
    return {
      rowId: row.id,
      updates: {},
      errors: {
        ...currentErrors,
        non_field_errors: [
          ...(currentErrors.non_field_errors || []),
          ENTITY_FETCH_ERROR
        ]
      }
    };
  }
};

// Fetch missing entity fields from the backend for batch upload edit.
// This retrieves the full entity data for records that exist and merges
// the CSV data with the existing data, so users see complete records.
//
// Returns an object with a Promise for each row with an ID or external_id (by row id),
// which resolves with the row update ({ rowId, updates, errors }) when it's fetched, or
// with null if the entity doesn't exist.
//
// metadata is the entity OPTIONS metadata (used to decode the entity data), if available.
export const fetchMissingEntityFields = (rows, entityType, metadata, entityCategoryTypes) => {
  // Filter rows that have an ID or external_id (existing entities to update)
  const rowsToFetch = rows.filter(row => row.data.id || row.data.external_id);
  const { promises, resolvers } = createRowPromises(rowsToFetch);

  processInBatches(rowsToFetch, async row => {
    resolvers[row.id](await fetchEntityRow(row, entityType, metadata, entityCategoryTypes));
  }, 10, 500);

  return promises;
};
